import { useIntl } from 'react-intl';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Tooltip from '@mui/material/Tooltip';
import GestureIcon from '@mui/icons-material/Gesture';
import RepeaterList from '../../components/RepeaterList';
import { isSeededRow } from './sketchStrata';
import type { StratumRow } from './stratumRows';

// ---------------------------------------------------------------------------
/**
 * The Schichten list editor, shared by the Planum and Profil forms — a
 * RepeaterList like the Farben and Beimengungen above it.
 *
 * WHICH MEASURES SHOW is the caller's decision: a Planum measures a layer by
 * Länge, Breite and — for a round one — Durchmesser, a Profil by Breite und
 * Tiefe. The row keeps them all either way, so a Schicht seeded on one and
 * measured on the other loses nothing.
 */
// ---------------------------------------------------------------------------

export default function StratumListEditor({
    rows,
    onChange,
    showLength,
    showDepth,
    showDiameter = false
}: {
    rows: StratumRow[];
    onChange: (rows: StratumRow[]) => void;
    /** Länge — a Planum measure. */
    showLength: boolean;
    /** Tiefe — a Profil measure. */
    showDepth: boolean;
    /** Durchmesser — a Planum measure, for a round layer. */
    showDiameter?: boolean;
}) {
    const intl = useIntl();
    const cm = intl.formatMessage({ id: 'common.centimetres' });
    const fromSketch = intl.formatMessage({ id: 'planum.stratum.fromSketch' });

    /** One measure cell — the unit sits in the field, not in the value. */
    function measure(
        row: StratumRow,
        update: (patch: Partial<StratumRow>) => void,
        key: 'length' | 'width' | 'depth' | 'diameter'
    ) {
        return (
            <TextField
                value={row[key] ?? ''}
                onChange={(event) => update({ [key]: event.target.value })}
                label={intl.formatMessage({ id: `planum.field.${key}` })}
                slotProps={{
                    input: {
                        endAdornment: (
                            <InputAdornment position="end">{cm}</InputAdornment>
                        )
                    }
                }}
                sx={{ width: 140, flexShrink: 0 }}
            />
        );
    }

    return (
        <RepeaterList
            rows={rows}
            onChange={onChange}
            blankRow={{ name: '', length: '', width: '', depth: '' }}
            labelId="planum.field.strata"
            emptyId="planum.stratum.empty"
            addId="planum.stratum.add"
            removeId="planum.stratum.remove"
            band
            // A Schicht seeded from a sketch carries a name as long as its
            // drawing's ("Ostprofil - Schicht A"), and three fields beside it
            // do not fit a phone.
            wrap
            renderRow={(row, update) => (
                <>
                    <TextField
                        value={row.name}
                        onChange={(event) =>
                            update({ name: event.target.value })
                        }
                        label={intl.formatMessage({
                            id: 'planum.field.stratumName'
                        })}
                        slotProps={{
                            /**
                             * The sketch's own icon, on the rows the sketch
                             * wrote — so a name that appeared by itself is
                             * recognisable as such, and a row typed by hand
                             * stays visibly the recorder's. The field is
                             * editable either way: renaming here takes the
                             * name over, it does not cut the row loose.
                             */
                            input: isSeededRow(row)
                                ? {
                                      startAdornment: (
                                          <InputAdornment position="start">
                                              <Tooltip title={fromSketch}>
                                                  <GestureIcon
                                                      fontSize="small"
                                                      color="action"
                                                      titleAccess={fromSketch}
                                                  />
                                              </Tooltip>
                                          </InputAdornment>
                                      )
                                  }
                                : undefined
                        }}
                        sx={{ flex: 1, minWidth: 180 }}
                    />
                    {showLength && measure(row, update, 'length')}
                    {measure(row, update, 'width')}
                    {showDepth && measure(row, update, 'depth')}
                    {showDiameter && measure(row, update, 'diameter')}
                </>
            )}
        />
    );
}
